/**
 * DocumentModal Helpers — Types, Constants, and Utility Functions
 *
 * Extracted from DocumentModal.tsx to keep the main component file under
 * the 500KB Babel code-generator threshold.
 */

import type React from 'react';
import {
  type Document,
  type DocumentType,
  type DocumentLineItem,
  type DocumentStatus,
} from '@/hooks/useDocumentsWorkItems';
import {
  type ReceiptAiPayload,
  type ReceiptAiValidation,
} from '@/lib/receiptAiAnalysis';
import { type SalesIntakePreview } from '@/lib/finance/salesIntakeAnalysis';
import { type InvoiceBatchRowFields } from '@/lib/finance/invoiceListUi';
import {
  DOCUMENT_TYPE_LABELS,
  DEFAULT_UNIFIED_STYLES,
  DEFAULT_UNIFIED_LAYOUT,
  getDefaultSections,
  getDefaultTableColumns,
  type DocumentType as UnifiedReportDocumentType,
  type TemplateTableColumn,
  type UnifiedTemplate,
  type UnifiedTemplateSection,
} from '@/lib/reports';
import type { FinalActionGateContext } from '@/config/document-modal/finalActionGates';
import { authService } from '@/lib/authService';
import { getDateStrSAST } from '@/lib/sastTime';
import type { NormalizedFinanceTierRestrictions } from '@/lib/finance/tierAccess';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

type StructuredAttachmentPreviewRow = {
  date: string;
  description: string;
  reference: string;
  amount: number;
  type: 'debit' | 'credit';
  balance: number | null;
};

type StructuredAttachmentPreview = {
  bank?: string;
  format: string;
  total_rows: number;
  parsed_rows: number;
  duplicate_rows: number;
  start_date?: string;
  end_date?: string;
  opening_balance?: number | null;
  closing_balance?: number | null;
  warnings: string[];
  rows: StructuredAttachmentPreviewRow[];
};

type StructuredAttachmentPreviewResponse = {
  bank?: string;
  format?: string;
  total_rows?: number;
  parsed_rows?: number;
  duplicate_rows?: number;
  start_date?: string | Date | null;
  end_date?: string | Date | null;
  opening_balance?: number | null;
  closing_balance?: number | null;
  warnings?: string[];
  errors?: string[];
  transactions?: Array<{
    date: string | Date;
    description: string;
    reference?: string | null;
    amount: number;
    transaction_type: 'debit' | 'credit';
    balance?: number | null;
  }>;
};

type AttachmentUploadStatus = 'staged' | 'uploading' | 'uploaded' | 'failed';

type DocumentAttachmentOcrLine = {
  description?: string;
  quantity?: number;
  unit_price?: number;
};

type DocumentAttachmentOcrResult = {
  total_amount?: number;
  vendor_name?: string;
  date?: string;
  items?: DocumentAttachmentOcrLine[];
  confidence?: number;
  validation?: ReceiptAiValidation | null;
};

type PersistedDocumentAttachmentMetadata = {
  source: 'document_modal';
  version: 1;
  file_name: string;
  file_type: string;
  mime_type: string;
  persisted_at?: string;
  persisted_via?: 'auto_save' | 'manual_save';
  analysis_error?: string;
  ocr_result?: DocumentAttachmentOcrResult;
  sales_result?: SalesIntakePreview;
  structured_result?: StructuredAttachmentPreview;
};

type PersistedWorkItemAttachment = {
  id: string | number;
  work_item_id?: string;
  file_name?: string | null;
  file_path?: string | null;
  file_size?: number | null;
  file_type?: string | null;
  mime_type?: string | null;
  category?: string | null;
  metadata?: unknown;
};

type DocumentPhotoAttachment = {
  id: string;
  attachment_id?: string;
  work_item_id?: string;
  file_blob?: File;
  data_url?: string;
  file_path?: string;
  file_name: string;
  file_type: string;
  mime_type: string;
  file_size?: number;
  ocr_result?: DocumentAttachmentOcrResult;
  sales_result?: SalesIntakePreview;
  structured_result?: StructuredAttachmentPreview;
  analysis_error?: string;
  ocr_processing: boolean;
  upload_status: AttachmentUploadStatus;
  upload_error?: string;
  is_persisted: boolean;
  sync_required: boolean;
  category?: string;
};

const PURCHASE_DOCUMENT_TYPES = ['purchase', 'purchase_order', 'supplier_invoice', 'supplier_delivery_note', 'expense', 'grn', 'supplier_credit', 'supplier_quote', 'purchase_requisition', 'rfq', 'purchase_return', 'din'] as const;

const DOCUMENT_ATTACHMENT_CATEGORY = 'finance_document_source';
const MAX_ATTACHMENT_COUNT = 20;
const MAX_ATTACHMENT_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const MAX_ATTACHMENT_TOTAL_BYTES = 100 * 1024 * 1024;
const MAX_ATTACHMENT_ZIP_ENTRIES = 25;
const MAX_ATTACHMENT_ZIP_TOTAL_BYTES = 100 * 1024 * 1024;
const MAX_ATTACHMENT_ANALYSIS_CONCURRENCY = 3;
const ATTACHMENT_ANALYSIS_TIMEOUT_MS = 45_000;
const OCR_SAFE_TOTAL_CONFIDENCE_THRESHOLD = 0.85;
const SALES_CONFIRM_SIGNAL_CONFIDENCE_THRESHOLD = 0.7;

const ATTACHMENT_ANALYSIS_ACCEPT_PURCHASE = 'image/*,.csv,.pdf,.zip,.ofx,.qfx,.qif,.mt940,.sta,.940,.txt,.xlsx,.xls';
const ATTACHMENT_ANALYSIS_ACCEPT_SALES = 'image/*,.csv,.pdf,.zip,.txt,.xlsx,.xls,.docx,.json,.md,.xml,.yaml,.yml,.html,.log';
const ATTACHMENT_PURCHASE_STRUCTURED_EXTENSIONS = new Set([
  'csv',
  'pdf',
  'ofx',
  'qfx',
  'qif',
  'mt940',
  'sta',
  '940',
  'txt',
  'xlsx',
  'xls',
]);
const ATTACHMENT_SALES_STRUCTURED_EXTENSIONS = new Set([
  'csv',
  'pdf',
  'txt',
  'xlsx',
  'xls',
  'docx',
  'json',
  'md',
  'xml',
  'yaml',
  'yml',
  'html',
  'log',
]);
const ATTACHMENT_IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif']);

function getAttachmentFileExtension(fileName: string): string {
  return fileName.toLowerCase().split('.').pop() || '';
}

function formatStatementDateValue(value: string | Date | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return getDateStrSAST(parsed);
}

function formatAttachmentFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) {
    return '0 Bytes';
  }

  const units = ['Bytes', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${Math.round(value * 100) / 100} ${units[exponent]}`;
}

function isImageLikeAttachment(fileName: string, mimeType?: string | null): boolean {
  return Boolean(
    (mimeType && mimeType.startsWith('image/'))
    || ATTACHMENT_IMAGE_EXTENSIONS.has(getAttachmentFileExtension(fileName))
  );
}

function parsePersistedDocumentAttachmentMetadata(raw: unknown): PersistedDocumentAttachmentMetadata | null {
  if (!raw) {
    return null;
  }

  const parsed = typeof raw === 'string'
    ? (() => {
      try {
        return JSON.parse(raw) as unknown;
      } catch {
        return null;
      }
    })()
    : raw;

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  const candidate = parsed as Partial<PersistedDocumentAttachmentMetadata> & { source?: unknown };
  if (candidate.source !== 'document_modal') {
    return null;
  }

  return {
    source: 'document_modal',
    version: 1,
    file_name: typeof candidate.file_name === 'string' ? candidate.file_name : '',
    file_type: typeof candidate.file_type === 'string' ? candidate.file_type : '',
    mime_type: typeof candidate.mime_type === 'string' ? candidate.mime_type : 'application/octet-stream',
    persisted_at: typeof candidate.persisted_at === 'string' ? candidate.persisted_at : undefined,
    persisted_via: candidate.persisted_via === 'auto_save' ? 'auto_save' : 'manual_save',
    analysis_error: typeof candidate.analysis_error === 'string' ? candidate.analysis_error : undefined,
    ocr_result: candidate.ocr_result,
    sales_result: candidate.sales_result,
    structured_result: candidate.structured_result,
  };
}

function buildAttachmentOcrResult(
  receipt: ReceiptAiPayload,
  validation: ReceiptAiValidation | null,
): DocumentAttachmentOcrResult {
  return {
    total_amount: receipt.total_amount ?? undefined,
    vendor_name: receipt.vendor_name ?? undefined,
    date: receipt.receipt_date ?? undefined,
    confidence: Number.isFinite(receipt.confidence) ? receipt.confidence : undefined,
    validation,
    items: (receipt.line_items || []).map((lineItem) => ({
      description: lineItem.description,
      quantity: lineItem.quantity,
      unit_price: lineItem.unit_price,
    })),
  };
}

function getAttachmentConfidence(attachment: Pick<DocumentPhotoAttachment, 'ocr_result' | 'sales_result'>): number | null {
  const ocrConfidence = attachment.ocr_result?.confidence;
  if (typeof ocrConfidence === 'number' && Number.isFinite(ocrConfidence)) {
    return ocrConfidence;
  }

  const salesConfidence = attachment.sales_result?.confidence;
  if (typeof salesConfidence === 'number' && Number.isFinite(salesConfidence)) {
    return salesConfidence;
  }

  return null;
}

function shouldConfirmDetectedTotalApply(attachment: DocumentPhotoAttachment): boolean {
  if (attachment.structured_result) {
    return false;
  }

  if (attachment.sales_result) {
    const salesConfidence = attachment.sales_result.confidence;
    return !Number.isFinite(salesConfidence) || salesConfidence < SALES_CONFIRM_SIGNAL_CONFIDENCE_THRESHOLD;
  }

  const confidence = attachment.ocr_result?.confidence;
  const validation = attachment.ocr_result?.validation;
  const validationMatch = validation?.match;
  const isValidationSafe = !validationMatch || ['exact', 'close', 'no_declared'].includes(validationMatch);
  const normalizedConfidence = typeof confidence === 'number' && Number.isFinite(confidence)
    ? confidence
    : null;

  return normalizedConfidence == null
    || normalizedConfidence < OCR_SAFE_TOTAL_CONFIDENCE_THRESHOLD
    || !isValidationSafe;
}

function buildDetectedSignalLabel(confidence: number | null): string {
  if (confidence == null) {
    return 'Confidence unavailable';
  }

  return `Confidence ${Math.round(confidence * 100)}%`;
}

export interface DocumentConfig {
  doc_type: DocumentType;

  labels: {
    title: string;
    titleNew: string;
    titleEdit: string;
    numberField: string;
    numberPlaceholder: string;
    dateField: string;
    contactLabel: string;
    saveButton: string;
  };

  fields: {
    showDocumentNumber?: boolean;
    documentNumberReadOnly?: boolean;
    showMainDate?: boolean;
    showDueDate?: boolean;
    showExpiryDate?: boolean;
    showTransactionDate?: boolean;
    showExpectedShipDate?: boolean;
    showExpectedDeliveryDate?: boolean;
    showReceivedDate?: boolean;
    showDeliveryDate?: boolean;
    showStatus?: boolean;
    /** @deprecated Use financeStatusConfig.getFinanceStatuses(itemType) instead — SSOT loaded from types.yaml */
    statusOptions?: Array<{ value: string; label: string }>;
    /** @deprecated Use financeStatusConfig + documentLifecycle for status transitions — SSOT loaded from types.yaml */
    userEditableStatuses?: string[];
    showFulfillmentStatus?: boolean;
    fulfillmentStatusOptions?: Array<{ value: string; label: string }>;
    showPaymentStatus?: boolean;
    paymentStatusOptions?: Array<{ value: string; label: string }>;
    showApprovalStatus?: boolean;
    approvalStatusOptions?: Array<{ value: string; label: string }>;
    showQualityCheckStatus?: boolean;
    qualityCheckStatusOptions?: Array<{ value: string; label: string }>;
    showInvoiceType?: boolean;
    invoiceTypeOptions?: Array<{ value: string; label: string }>;
    showBillType?: boolean;
    billTypeOptions?: Array<{ value: string; label: string }>;
    showPaymentTerms?: boolean;
    showAmountPaid?: boolean;
    showBalanceDue?: boolean;
    showDiscount?: boolean;
    showMarkup?: boolean;
    showShippingCost?: boolean;
    showReference?: boolean;
    showCustomerPO?: boolean;
    showSupplierInvoiceNumber?: boolean;
    showSupplierReference?: boolean;
    showOriginalInvoice?: boolean;
    showPackingSlipNumber?: boolean;
    showTrackingNumber?: boolean;
    showCarrier?: boolean;
    showWarehouseLocation?: boolean;
    showInspector?: boolean;
    showShippingMethod?: boolean;
    showShippingAddress?: boolean;
    showDeliveryAddress?: boolean;
    showSignedBy?: boolean;
    showNotes?: boolean;
    showInternalNotes?: boolean;
    showTermsConditions?: boolean;
    showDeliveryInstructions?: boolean;
    /**
     * Contact types offered by UnifiedContactLookup for this document.
     * Defaults to ['supplier'] for purchase documents, ['customer'] otherwise.
     * Property-finance docs may widen this (e.g. tenants, schemes, trustees)
     * to any contact_type managed by ContactManagementHub.
     */
    contactTypes?: string[];
  };

  conversions?: Array<{
    targetType: DocumentType;
    label: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    condition?: (doc: Document) => boolean;
  }>;

  /**
   * SSOT configuration for the final action dropdown in DocumentModal.
   * Defines which buttons, convert commands and gating conditions apply per document type.
   */
  finalActionMenu?: FinalActionMenuConfig;
}

/** Machine-readable action types for the final action dropdown. */
export type FinalActionMenuActionType = 'preview' | 'send' | 'complete' | 'convert' | 'recurring' | 'submit_for_approval' | 'add_to_batch';

/** One item inside the final action dropdown. */
export interface FinalActionMenuItem {
  type: FinalActionMenuActionType;
  label: string;
  /** Optional Lucide icon component. Defaults are provided for known action types. */
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  /** Only show this item when the condition is true. Defaults to always visible. */
  condition?: (doc: Partial<Document>, items: DocumentLineItem[]) => boolean;
  /** Disable this item (in addition to the menu-wide gate). */
  disabled?: (doc: Partial<Document>, items: DocumentLineItem[]) => boolean;
  /** For type='convert', the target document type. */
  targetType?: DocumentType;
  /** Visual tone used to color the item icon. */
  tone?: 'default' | 'primary' | 'success' | 'danger' | 'warning';
}

/** Full final action dropdown configuration for a document type. */
export interface FinalActionMenuConfig {
  /** Gate applied to the entire menu. If it returns pass=false, the dropdown toggle and all items are disabled. */
  gate?: (doc: Partial<Document>, items: DocumentLineItem[], ctx?: FinalActionGateContext) => { pass: boolean; message?: string };
  /** Ordered list of actions to render in the dropdown. */
  items: FinalActionMenuItem[];
}

export interface DocumentModalProps {
  doc_type: DocumentType;
  document?: Document | null;
  config: DocumentConfig;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (document: Document) => void;
  onConverted?: (result: DocumentConversionResult) => void;
  onDelete?: (id: string) => void;
  /**
   * Open a related document (e.g. another DIN/EXP/BIL for the same supplier,
   * clicked from the supplier history panel or the duplicate warning). Called
   * only for editable documents so the parent can swap the open document;
   * read-only documents fall back to a nested DocumentViewModal.
   */
  onOpenDocument?: (document: Document) => void;
  permissions?: {
    can_view?: boolean;
    can_create?: boolean;
    can_edit?: boolean;
    can_delete?: boolean;
    can_admin?: boolean;
  };
  financeTierRestrictions?: NormalizedFinanceTierRestrictions | null;
  presentationMode?: 'default' | 'smarttask-mobile' | 'finance-mobile';
  financeContext?: {
    enabled: boolean;
    docType?: string;
    onOpenActivityLog?: () => void;
    hideTimingSection?: boolean;
    enhanceRelationshipTree?: boolean;
  };
}

type HydrationSourceLineItem = Partial<DocumentLineItem> & {
  item_name?: string;
  unit_of_measure?: string;
  unit_cost?: number | string | null;
  total_cost?: number | string | null;
  job_number?: string | null;
  appointment_number?: string | null;
};

type DocumentConversionResult = {
  success: boolean;
  document?: Record<string, unknown>;
  targetType?: DocumentType;
  error?: string;
  [key: string]: unknown;
};

type DocumentFormState = Partial<Document> & {
  tracking_number?: string;
  warehouse_id?: number | null;
};

type DocumentSidebarSource = Document & {
  priority?: string;
  assigned_to?: string | number | null;
};

type WorkItemPriority = 'low' | 'medium' | 'high' | 'urgent' | 'critical';

interface DocumentTotalsCalculation {
  subtotal?: number;
  tax_total?: number;
  total_tax?: number;
  discount_total?: number;
  grand_total?: number;
  total?: number;
}

type CalculateTotalsResponse =
  | DocumentTotalsCalculation
  | { totals?: DocumentTotalsCalculation | null }
  | null;

interface FinanceListRequest {
  action: 'list';
  table: 'work_items';
  where: {
    item_type: { operator: 'in'; value: DocumentType[] };
    status: { operator: 'in'; value: string[] };
    contact_id?: number;
  };
  orderBy: Array<{ field: 'created_at'; direction: 'desc' }>;
  limit: number;
  search?: string;
  searchFields?: string[];
}

type InsertMenuPlacement = 'top' | 'bottom';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return fallback;
}

function isWrappedTotalsResponse(
  response: CalculateTotalsResponse,
): response is { totals?: DocumentTotalsCalculation | null } {
  return Boolean(response && typeof response === 'object' && 'totals' in response);
}

function normalizeCalculatedTotals(response: CalculateTotalsResponse): DocumentTotalsCalculation | null {
  if (!response) {
    return null;
  }

  return isWrappedTotalsResponse(response)
    ? response.totals ?? null
    : response;
}

function normalizeWorkItemPriority(priority?: string | null): WorkItemPriority {
  switch (priority) {
    case 'low':
    case 'medium':
    case 'high':
    case 'urgent':
    case 'critical':
      return priority;
    default:
      return 'medium';
  }
}

function hasMeaningfulLineItemContent(item: DocumentLineItem): boolean {
  const description = item.description?.trim();
  const lineType = item.line_type || 'item';

  if (lineType === 'spacer') {
    return false;
  }

  if (lineType === 'header' || lineType === 'comment') {
    return Boolean(description);
  }

  if (lineType === 'subtotal') {
    return Boolean(description || Number(item.line_total || 0) !== 0);
  }

  return Boolean(
    description
    || item.item_id
    || item.item_code?.trim()
    || Number(item.quantity || 0) !== 0
    || Number(item.unit_price || 0) !== 0
    || Number(item.line_total || 0) !== 0
  );
}

type PrintPreviewSectionKey =
  | 'header'
  | 'companyInfo'
  | 'customerInfo'
  | 'documentDetails'
  | 'itemsTable'
  | 'totals'
  | 'notes'
  | 'terms'
  | 'bankDetails'
  | 'signature'
  | 'footer';

interface PrintPreviewSectionState {
  show: boolean;
}

interface PrintPreviewColumnState {
  show: boolean;
  label: string;
}

interface PrintPreviewTemplate {
  id?: number;
  name: string;
  styles: {
    primaryColor: string;
    textColor: string;
    tableBorderColor: string;
    tableHeaderBgColor: string;
    fontFamily: string;
    fontSize: number;
    lineHeight: number;
  };
  layout: {
    showLogo: boolean;
    logoSize: { width: number; height: number };
    showPaymentDetails: boolean;
    margins: { top: number; right: number; bottom: number; left: number };
  };
  sections: Record<PrintPreviewSectionKey, PrintPreviewSectionState>;
  table_columns: {
    description: PrintPreviewColumnState;
    quantity: PrintPreviewColumnState;
    unitPrice: PrintPreviewColumnState;
    tax: PrintPreviewColumnState;
    total: PrintPreviewColumnState;
  };
  custom_content?: {
    footerText?: string;
  };
}

const PRINT_PREVIEW_SECTION_KEYS: PrintPreviewSectionKey[] = [
  'header',
  'companyInfo',
  'customerInfo',
  'documentDetails',
  'itemsTable',
  'totals',
  'notes',
  'terms',
  'bankDetails',
  'signature',
  'footer',
];

const UNIFIED_FONT_FAMILY_TO_CSS: Record<string, string> = {
  helvetica: 'Helvetica, Arial, sans-serif',
  times: '"Times New Roman", Times, serif',
  courier: '"Courier New", Courier, monospace',
};

function buildInvoicePreviewLockProbe(
  documentRow: Document | null | undefined,
  formDraft: Partial<Document>,
  itemType = 'invoice',
): InvoiceBatchRowFields {
  const d = documentRow;
  const f = formDraft;
  const cf = (f.custom_fields ?? d?.custom_fields) as Record<string, unknown> | undefined;

  const readString = (
    keys: readonly string[],
  ): string | null => {
    const dRecord = d ? (d as unknown as Record<string, unknown>) : null;
    const fRecord = f as Record<string, unknown>;

    for (const key of keys) {
      const fromD = dRecord?.[key];
      const fromF = fRecord[key];
      const fromCf = cf?.[key];
      const raw = fromD ?? fromF ?? fromCf;
      if (raw != null && String(raw).trim() !== '') {
        return String(raw);
      }
    }
    return null;
  };

  return {
    item_type: itemType,
    status: String(f.status ?? d?.status ?? ''),
    batch_id: readString(['batch_id']),
    batch_status: readString(['batch_status']),
    batch_number: readString(['batch_number', 'invoice_batch_number']),
    payment_status: String(f.payment_status ?? d?.payment_status ?? ''),
    balance_due: f.balance_due ?? d?.balance_due ?? null,
    grand_total: f.grand_total ?? d?.grand_total ?? null,
    metadata: (d as { metadata?: unknown } | undefined)?.metadata
      ?? (f as { metadata?: unknown }).metadata,
    custom_fields: cf ?? null,
  };
}

function findSectionEnabled(
  sections: UnifiedTemplateSection[] | undefined,
  key: string,
  fallback: boolean,
): boolean {
  const match = sections?.find((section) => section.id === key);
  return match?.enabled ?? fallback;
}

function findColumn(
  columns: TemplateTableColumn[] | undefined,
  key: string,
  fallbackLabel: string,
  fallbackShow = true,
): PrintPreviewColumnState {
  const match = columns?.find((column) => column.key === key);
  return {
    show: match?.show ?? fallbackShow,
    label: match?.label || fallbackLabel,
  };
}

function buildGenericPrintPreviewTemplate(docType: DocumentType): PrintPreviewTemplate {
  const label = docType.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  return {
    name: `${label} Default Template`,
    styles: {
      primaryColor: '#1e3a5f',
      textColor: '#333333',
      tableBorderColor: '#e5e7eb',
      tableHeaderBgColor: '#f3f4f6',
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontSize: 9,
      lineHeight: 1.5,
    },
    layout: {
      showLogo: true,
      logoSize: { width: 150, height: 60 },
      showPaymentDetails: true,
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
    },
    sections: PRINT_PREVIEW_SECTION_KEYS.reduce<Record<PrintPreviewSectionKey, PrintPreviewSectionState>>((acc, key) => {
      acc[key] = { show: true };
      return acc;
    }, {} as Record<PrintPreviewSectionKey, PrintPreviewSectionState>),
    table_columns: {
      description: { show: true, label: 'Description' },
      quantity: { show: true, label: 'Qty' },
      unitPrice: { show: true, label: 'Unit Price' },
      tax: { show: true, label: 'Tax' },
      total: { show: true, label: 'Total' },
    },
    custom_content: {
      footerText: 'Thank you for your business',
    },
  };
}

function createPrintPreviewTemplateFromUnified(
  docType: UnifiedReportDocumentType,
  template?: UnifiedTemplate | null,
): PrintPreviewTemplate {
  const fallbackSections = getDefaultSections(docType);
  const fallbackColumns = getDefaultTableColumns(docType);
  const activeTemplate: Partial<UnifiedTemplate> & {
    id?: number;
    name: string;
    styles: typeof DEFAULT_UNIFIED_STYLES;
    layout: typeof DEFAULT_UNIFIED_LAYOUT;
    sections: UnifiedTemplateSection[];
    table_columns?: TemplateTableColumn[];
    custom_content?: Record<string, string>;
  } = template || {
    id: undefined,
    name: `Default ${DOCUMENT_TYPE_LABELS[docType] || docType} Template`,
    styles: { ...DEFAULT_UNIFIED_STYLES },
    layout: { ...DEFAULT_UNIFIED_LAYOUT },
    sections: fallbackSections,
    table_columns: fallbackColumns,
    custom_content: {},
  };

  return {
    id: activeTemplate.id,
    name: activeTemplate.name || `Default ${DOCUMENT_TYPE_LABELS[docType] || docType} Template`,
    styles: {
      primaryColor: activeTemplate.styles?.primaryColor || DEFAULT_UNIFIED_STYLES.primaryColor,
      textColor: activeTemplate.styles?.textColor || DEFAULT_UNIFIED_STYLES.textColor,
      tableBorderColor: activeTemplate.styles?.tableBorderColor || DEFAULT_UNIFIED_STYLES.tableBorderColor || '#e5e7eb',
      tableHeaderBgColor: activeTemplate.styles?.tableHeaderBgColor || DEFAULT_UNIFIED_STYLES.tableHeaderBgColor || '#f3f4f6',
      fontFamily: UNIFIED_FONT_FAMILY_TO_CSS[activeTemplate.styles?.fontFamily || 'helvetica'] || 'Helvetica, Arial, sans-serif',
      fontSize: activeTemplate.styles?.fontSize || DEFAULT_UNIFIED_STYLES.fontSize,
      lineHeight: activeTemplate.styles?.lineHeight || DEFAULT_UNIFIED_STYLES.lineHeight,
    },
    layout: {
      showLogo: true,
      logoSize: { width: 150, height: 60 },
      showPaymentDetails: findSectionEnabled(activeTemplate.sections, 'payment_info', findSectionEnabled(activeTemplate.sections, 'bankDetails', true)),
      margins: activeTemplate.styles?.margins || DEFAULT_UNIFIED_STYLES.margins,
    },
    sections: {
      header: { show: findSectionEnabled(activeTemplate.sections, 'header', true) },
      companyInfo: { show: findSectionEnabled(activeTemplate.sections, 'company_info', true) },
      customerInfo: { show: findSectionEnabled(activeTemplate.sections, 'contact_info', true) },
      documentDetails: { show: findSectionEnabled(activeTemplate.sections, 'summary_cards', true) },
      itemsTable: { show: findSectionEnabled(activeTemplate.sections, 'line_items_table', true) },
      totals: { show: findSectionEnabled(activeTemplate.sections, 'totals', true) },
      notes: { show: findSectionEnabled(activeTemplate.sections, 'notes', true) },
      terms: { show: findSectionEnabled(activeTemplate.sections, 'terms_notes', true) },
      bankDetails: { show: findSectionEnabled(activeTemplate.sections, 'payment_info', true) },
      signature: { show: findSectionEnabled(activeTemplate.sections, 'signature', false) },
      footer: { show: findSectionEnabled(activeTemplate.sections, 'footer', true) },
    },
    table_columns: {
      description: findColumn(activeTemplate.table_columns, 'description', 'Description'),
      quantity: findColumn(activeTemplate.table_columns, 'quantity', 'Qty'),
      unitPrice: findColumn(activeTemplate.table_columns, 'unitPrice', 'Unit Price'),
      tax: findColumn(activeTemplate.table_columns, 'tax', 'Tax'),
      total: findColumn(activeTemplate.table_columns, 'total', 'Total'),
    },
    custom_content: {
      footerText: activeTemplate.custom_content?.['footerText'] || 'Thank you for your business',
    },
  };
}

function readStoredUnifiedTemplatePreference(docType: UnifiedReportDocumentType): number | null | undefined {
  if (typeof window === 'undefined') return undefined;
  const raw = window.localStorage.getItem(`ta_document_report_template:${docType}`);
  if (!raw) return undefined;
  if (raw === 'default') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

async function loadUnifiedPrintPreviewTemplate(docType: UnifiedReportDocumentType): Promise<PrintPreviewTemplate> {
  const token = authService.getTokens()?.access_token;
  const response = await fetch(`/api/v1/reports/templates?documentType=${docType}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error('Failed to load unified report templates');
  }

  const json = await response.json();
  if (!json.success) {
    throw new Error(json.error || 'Failed to load unified report templates');
  }

  const templates: UnifiedTemplate[] = Array.isArray(json.data?.templates) ? json.data.templates : [];
  const preferredTemplateId = readStoredUnifiedTemplatePreference(docType);
  const selectedTemplate = preferredTemplateId === null
    ? null
    : (preferredTemplateId !== undefined
      ? templates.find((template) => template.id === preferredTemplateId)
      : undefined)
    || templates.find((template) => template.is_default)
    || templates[0]
    || null;

  return createPrintPreviewTemplateFromUnified(docType, selectedTemplate);
}

// ============================================================================
// EXPORTS
// ============================================================================

export type {
  StructuredAttachmentPreviewRow,
  StructuredAttachmentPreview,
  StructuredAttachmentPreviewResponse,
  AttachmentUploadStatus,
  DocumentAttachmentOcrLine,
  DocumentAttachmentOcrResult,
  PersistedDocumentAttachmentMetadata,
  PersistedWorkItemAttachment,
  DocumentPhotoAttachment,
  HydrationSourceLineItem,
  DocumentConversionResult,
  DocumentFormState,
  DocumentSidebarSource,
  WorkItemPriority,
  DocumentTotalsCalculation,
  CalculateTotalsResponse,
  FinanceListRequest,
  InsertMenuPlacement,
  PrintPreviewSectionKey,
  PrintPreviewSectionState,
  PrintPreviewColumnState,
  PrintPreviewTemplate,
};

export {
  PURCHASE_DOCUMENT_TYPES,
  DOCUMENT_ATTACHMENT_CATEGORY,
  MAX_ATTACHMENT_COUNT,
  MAX_ATTACHMENT_FILE_SIZE_BYTES,
  MAX_ATTACHMENT_TOTAL_BYTES,
  MAX_ATTACHMENT_ZIP_ENTRIES,
  MAX_ATTACHMENT_ZIP_TOTAL_BYTES,
  MAX_ATTACHMENT_ANALYSIS_CONCURRENCY,
  ATTACHMENT_ANALYSIS_TIMEOUT_MS,
  OCR_SAFE_TOTAL_CONFIDENCE_THRESHOLD,
  SALES_CONFIRM_SIGNAL_CONFIDENCE_THRESHOLD,
  ATTACHMENT_ANALYSIS_ACCEPT_PURCHASE,
  ATTACHMENT_ANALYSIS_ACCEPT_SALES,
  ATTACHMENT_PURCHASE_STRUCTURED_EXTENSIONS,
  ATTACHMENT_SALES_STRUCTURED_EXTENSIONS,
  ATTACHMENT_IMAGE_EXTENSIONS,
  PRINT_PREVIEW_SECTION_KEYS,
  UNIFIED_FONT_FAMILY_TO_CSS,
  getAttachmentFileExtension,
  formatStatementDateValue,
  formatAttachmentFileSize,
  isImageLikeAttachment,
  parsePersistedDocumentAttachmentMetadata,
  buildAttachmentOcrResult,
  getAttachmentConfidence,
  shouldConfirmDetectedTotalApply,
  buildDetectedSignalLabel,
  getErrorMessage,
  isWrappedTotalsResponse,
  normalizeCalculatedTotals,
  normalizeWorkItemPriority,
  hasMeaningfulLineItemContent,
  buildInvoicePreviewLockProbe,
  findSectionEnabled,
  findColumn,
  buildGenericPrintPreviewTemplate,
  createPrintPreviewTemplateFromUnified,
  readStoredUnifiedTemplatePreference,
  loadUnifiedPrintPreviewTemplate,
};
